import { useEffect, useState, type FormEvent } from 'react'
import { useStatusDeReferencia } from '../../../api/queries/referencias'
import { Botao } from '../../../components/Botao'
import { CampoDeTexto } from '../../../components/campos'
import { ROTULO_ORIGEM, ROTULO_PRIORIDADE, ROTULO_STATUS } from '../../../lib/glossario'
import type { FiltrosDaLista as Criterios } from '../../../api/filtrosDeGmud'
import type { OrigemGmud, Prioridade, StatusGmud } from '../../../types/gmud'
import { GrupoDeCaixas, type OpcaoDeFiltro } from './GrupoDeCaixas'

interface Props {
  filtros: Criterios
  alterar: (mudanca: Partial<Criterios>) => void
}

const PRIORIDADES: OpcaoDeFiltro[] = Object.entries(ROTULO_PRIORIDADE).map(([valor, rotulo]) => ({ valor, rotulo }))

/**
 * Procedência com os dois valores escritos por extenso, e não uma única caixa "incluir
 * histórico": a caixa sozinha esconde qual é o padrão, e o padrão passou a esconder alguma coisa
 * — a lista limpa traz só o processo deste sistema. Aqui o usuário lê o recorte em vigor em vez
 * de deduzi-lo do que está desmarcado, e vê "Registro deste sistema" já marcado ao abrir o painel.
 */
const PROCEDENCIAS: OpcaoDeFiltro[] = [
  { valor: 'V2', rotulo: ROTULO_ORIGEM.V2 ?? 'V2' },
  { valor: 'MIGRADO_V1', rotulo: ROTULO_ORIGEM.MIGRADO_V1 ?? 'MIGRADO_V1' },
]

/** "As duas" é a lista de opções, não uma segunda lista de valores que possa envelhecer sozinha. */
const AS_DUAS = PROCEDENCIAS.map((opcao) => opcao.valor) as OrigemGmud[]

// A ajuda existe porque "nenhuma marcada" é o único estado que a tela resolve sozinha, e caixa
// que se marca sem o usuário ter clicado precisa dizer por quê. O texto é para quem não é de TI;
// o motivo técnico está em `procedencia`, logo abaixo.
const AJUDA_PROCEDENCIA =
  'Sem nenhuma marcada, a lista mostraria as duas procedências — o mesmo que marcar as duas. ' +
  'Por isso, ao desmarcar a última, as duas voltam marcadas.'

/**
 * Os campos do painel de filtros.
 *
 * As caixas aplicam na hora; os campos de texto só ao enviar. É a diferença entre uma escolha
 * (um clique, resultado imediato) e uma digitação (uma consulta por tecla, que castiga o banco
 * e faz a tabela tremer enquanto se escreve).
 *
 * @param props critérios correntes e a função que os altera
 * @returns o formulário de filtros
 */
export function CamposDeFiltro({ filtros, alterar }: Props) {
  const { data: status } = useStatusDeReferencia()
  const [chamado, setChamado] = useState(filtros.chamado)
  const [busca, setBusca] = useState(filtros.q)

  // Voltar pelo histórico do navegador muda a URL sem passar por este formulário;
  // sem isto, os campos de texto continuariam mostrando o que foi digitado antes.
  useEffect(() => setChamado(filtros.chamado), [filtros.chamado])
  useEffect(() => setBusca(filtros.q), [filtros.q])

  const opcoesDeStatus: OpcaoDeFiltro[] = (status ?? []).map((s) => ({
    valor: s.codigo,
    rotulo: ROTULO_STATUS[s.codigo] ?? s.rotulo,
  }))

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    alterar({ chamado, q: busca })
  }

  return (
    <form onSubmit={enviar} className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <CampoDeTexto
          rotulo="Número do chamado"
          ajuda="Busca por parte do número, como aparece no e-Niterói."
          inputMode="numeric"
          value={chamado}
          onChange={(e) => setChamado(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Buscar"
          ajuda="Procura no código da solicitação, no chamado e na justificativa."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      <GrupoDeCaixas
        legenda="Estado"
        opcoes={opcoesDeStatus}
        selecionados={filtros.status}
        aoAlternar={(valor, marcado) => alterar({ status: alternar(filtros.status, valor, marcado) as StatusGmud[] })}
      />

      <GrupoDeCaixas
        legenda="Prioridade"
        opcoes={PRIORIDADES}
        selecionados={filtros.prioridade}
        aoAlternar={(valor, marcado) =>
          alterar({ prioridade: alternar(filtros.prioridade, valor, marcado) as Prioridade[] })
        }
      />

      <GrupoDeCaixas
        legenda="De onde veio o registro"
        ajuda={AJUDA_PROCEDENCIA}
        opcoes={PROCEDENCIAS}
        selecionados={filtros.origem}
        aoAlternar={(valor, marcado) => alterar({ origem: procedencia(filtros.origem, valor, marcado) })}
      />

      <Botao type="submit" aparencia="primario">
        Aplicar
      </Botao>
    </form>
  )
}

function alternar(atuais: string[], valor: string, marcado: boolean): string[] {
  return marcado ? [...atuais, valor] : atuais.filter((v) => v !== valor)
}

/**
 * A procedência depois de marcar ou desmarcar uma caixa, com o vazio resolvido.
 *
 * Desmarcar a última caixa não pode deixar a tela num estado sem sentido. Vazio já quer dizer
 * "as duas" para a API, então é isso que as caixas passam a mostrar — a tela diz em voz alta o
 * que a consulta vai fazer. Escrever o vazio na URL seria pior: URL sem `origem` é o padrão
 * (`lerDaUrl`), e a caixa que o usuário acabou de desmarcar voltaria sozinha ao recarregar,
 * mostrando um recorte diferente do que a lista trouxe.
 */
function procedencia(atuais: OrigemGmud[], valor: string, marcado: boolean): OrigemGmud[] {
  const proxima = alternar(atuais, valor, marcado) as OrigemGmud[]
  return proxima.length > 0 ? proxima : [...AS_DUAS]
}
